import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { Delete, Edit, Home, Map } from "@material-ui/icons"
import React, { useState } from "react"
import { useSnackbar } from "../../../utils/notificacoes"
import { useEdicaoClienteContext } from "../context/useEdicaoClienteContext"
import type { EnderecoCliente } from "../models/edicaoClienteModel"
import { useCardStyles } from "./estilosCards"

const useStyles = makeStyles({
    celulaTruncada: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    semResultado: {
        textAlign: "center",
        color: "#7f8c8d",
    },
})

type FormularioEndereco = {
    logradouro: string
    numero: string
    complemento: string
    referencia: string
    bairro: string
    cidade: string
    cep: string
}

const formularioVazio: FormularioEndereco = {
    logradouro: "",
    numero: "",
    complemento: "",
    referencia: "",
    bairro: "",
    cidade: "",
    cep: "",
}

function paraFormulario(endereco: EnderecoCliente): FormularioEndereco {
    return {
        logradouro: endereco.logradouro ?? "",
        numero: endereco.numero ?? "",
        complemento: endereco.complemento ?? "",
        referencia: endereco.referencia ?? "",
        bairro: endereco.bairro ?? "",
        cidade: endereco.cidade ?? "",
        cep: endereco.cep ?? "",
    }
}

export function EnderecosCliente() {
    const classes = useCardStyles()
    const tabela = useStyles()
    const { dados, salvarEndereco, excluirEndereco } = useEdicaoClienteContext()
    const { enqueueSnackbar } = useSnackbar()

    const alertStart = (msg: string, tipo: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    const [emEdicao, setEmEdicao] = useState<EnderecoCliente | null>(null)
    const [formulario, setFormulario] = useState<FormularioEndereco>(formularioVazio)
    const [paraExcluir, setParaExcluir] = useState<EnderecoCliente | null>(null)

    const abrirEdicao = (endereco: EnderecoCliente) => {
        setEmEdicao(endereco)
        setFormulario(paraFormulario(endereco))
    }

    const handleCampo =
        (campo: keyof FormularioEndereco) => (e: React.ChangeEvent<HTMLInputElement>) => {
            const valor = e.target.value
            setFormulario(prev => ({ ...prev, [campo]: valor }))
        }

    const handleSalvarEndereco = async () => {
        if (!emEdicao) return
        if (!formulario.logradouro.trim()) {
            alertStart("O endereço é obrigatório.", "error")
            return
        }
        if (!formulario.numero.trim()) {
            alertStart("O número é obrigatório.", "error")
            return
        }
        if (!formulario.bairro.trim()) {
            alertStart("O bairro é obrigatório.", "error")
            return
        }
        if (!formulario.cidade.trim()) {
            alertStart("A cidade é obrigatória.", "error")
            return
        }

        try {
            const mensagem = await salvarEndereco(emEdicao.id, {
                logradouro: formulario.logradouro,
                numero: formulario.numero,
                complemento: formulario.complemento || null,
                referencia: formulario.referencia || null,
                bairro: formulario.bairro,
                cidade: formulario.cidade,
                cep: formulario.cep || null,
            })
            alertStart(mensagem, "success")
            setEmEdicao(null)
        } catch (error) {
            alertStart((error as Error).message || "Erro ao alterar o endereço", "error")
        }
    }

    const handleExcluirEndereco = async () => {
        if (!paraExcluir) return
        try {
            const mensagem = await excluirEndereco(paraExcluir.id)
            alertStart(mensagem, "success")
            setParaExcluir(null)
        } catch (error) {
            alertStart((error as Error).message || "Erro ao excluir o endereço", "error")
        }
    }

    return (
        <Paper className={classes.configCard}>
            <Box className={classes.cardHeader}>
                <Home className={classes.iconHeader} />
                <Typography variant="h6" className={classes.cardHeaderTitle}>
                    Endereços
                </Typography>
            </Box>

            <Box className={classes.cardContent}>
                <TableContainer style={{ maxHeight: 400, overflowX: "hidden" }}>
                    <Table
                        size="small"
                        stickyHeader
                        style={{ tableLayout: "fixed", width: "100%" }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell style={{ width: "8%" }}>Id</TableCell>
                                <TableCell style={{ width: "27%" }}>Logradouro</TableCell>
                                <TableCell style={{ width: "10%" }}>Número</TableCell>
                                <TableCell style={{ width: "17%" }}>Complemento</TableCell>
                                <TableCell style={{ width: "16%" }}>Bairro</TableCell>
                                <TableCell style={{ width: "12%" }}>Cidade</TableCell>
                                <TableCell style={{ width: "10%" }} align="center" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {dados.enderecos.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} className={tabela.semResultado}>
                                        O cliente não possui endereços cadastrados.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                dados.enderecos.map(endereco => (
                                    <TableRow key={endereco.id} hover>
                                        <TableCell>{endereco.id}</TableCell>
                                        <TableCell
                                            className={tabela.celulaTruncada}
                                            title={endereco.logradouro ?? ""}
                                        >
                                            {endereco.logradouro || "-"}
                                        </TableCell>
                                        <TableCell>{endereco.numero || "-"}</TableCell>
                                        <TableCell
                                            className={tabela.celulaTruncada}
                                            title={endereco.complemento ?? ""}
                                        >
                                            {endereco.complemento || "-"}
                                        </TableCell>
                                        <TableCell
                                            className={tabela.celulaTruncada}
                                            title={endereco.bairro ?? ""}
                                        >
                                            {endereco.bairro || "-"}
                                        </TableCell>
                                        <TableCell
                                            className={tabela.celulaTruncada}
                                            title={endereco.cidade ?? ""}
                                        >
                                            {endereco.cidade || "-"}
                                        </TableCell>
                                        <TableCell align="center" style={{ whiteSpace: "nowrap" }}>
                                            <Tooltip title="Editar">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => abrirEdicao(endereco)}
                                                >
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Excluir">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => setParaExcluir(endereco)}
                                                >
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            {endereco.latitude !== null &&
                                                endereco.longitude !== null && (
                                                    <Tooltip title="Ver no mapa">
                                                        <IconButton
                                                            size="small"
                                                            component="a"
                                                            href={`https://www.google.com.br/maps/place/${endereco.latitude},${endereco.longitude}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                        >
                                                            <Map fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            <Dialog
                open={emEdicao !== null}
                onClose={() => setEmEdicao(null)}
                fullWidth
                maxWidth="sm"
            >
                <DialogTitle>Endereço {emEdicao?.id}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                label="Endereço"
                                variant="outlined"
                                size="small"
                                fullWidth
                                required
                                value={formulario.logradouro}
                                onChange={handleCampo("logradouro")}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                label="Número"
                                variant="outlined"
                                size="small"
                                fullWidth
                                required
                                value={formulario.numero}
                                onChange={handleCampo("numero")}
                            />
                        </Grid>
                        <Grid item xs={8}>
                            <TextField
                                label="Complemento"
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={formulario.complemento}
                                onChange={handleCampo("complemento")}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Referência"
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={formulario.referencia}
                                onChange={handleCampo("referencia")}
                            />
                        </Grid>
                        <Grid item xs={12} sm={5}>
                            <TextField
                                label="Bairro"
                                variant="outlined"
                                size="small"
                                fullWidth
                                required
                                value={formulario.bairro}
                                onChange={handleCampo("bairro")}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                label="Cidade"
                                variant="outlined"
                                size="small"
                                fullWidth
                                required
                                value={formulario.cidade}
                                onChange={handleCampo("cidade")}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="CEP"
                                variant="outlined"
                                size="small"
                                fullWidth
                                inputProps={{ maxLength: 8 }}
                                value={formulario.cep}
                                onChange={handleCampo("cep")}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEmEdicao(null)}>Cancelar</Button>
                    <Button
                        variant="contained"
                        className={classes.botaoSalvar}
                        onClick={handleSalvarEndereco}
                    >
                        Atualizar
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={paraExcluir !== null} onClose={() => setParaExcluir(null)}>
                <DialogTitle>Excluir endereço</DialogTitle>
                <DialogContent>
                    <Typography>
                        Tem certeza que deseja excluir o endereço {paraExcluir?.id} —{" "}
                        {paraExcluir?.logradouro}, {paraExcluir?.numero}?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setParaExcluir(null)}>Cancelar</Button>
                    <Button color="secondary" variant="contained" onClick={handleExcluirEndereco}>
                        Excluir
                    </Button>
                </DialogActions>
            </Dialog>
        </Paper>
    )
}
